import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { importDeckList } from '../data/redux/slices/importSlice';
import { validateDeckImportUrl } from '../utils/deckImportUrl';

// Deep-link import: landing on /?ingest=<deck url> kicks off the same
// import flow as pasting the URL into the submit form. The import slice's
// done handler already routes to /details/deck/<id>, so once the import is
// dispatched the rest of the journey is the normal one.
export function useIngestDeepLink() {
    const dispatch = useDispatch();
    const isImporting = useSelector((state) => state?.import?.isImporting);
    const hasFiredRef = useRef(false);

    useEffect(() => {
        if (hasFiredRef.current || isImporting) return;

        const params = new URLSearchParams(window.location.search || '');
        const ingestUrl = params.get('ingest')?.trim();
        if (!ingestUrl) return;

        hasFiredRef.current = true;

        // Strip the param via replaceState (not router navigation) so a
        // refresh or back-button visit doesn't re-run the import, and so we
        // don't fight AppBody's redux-driven route sync.
        params.delete('ingest');
        const query = params.toString();
        window.history.replaceState(
            {},
            null,
            `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
        );

        const validationError = validateDeckImportUrl(ingestUrl);
        if (validationError) {
            toast(validationError);
            return;
        }

        dispatch(importDeckList(ingestUrl));
        try {
            logEvent(getAnalytics(), 'import_start', { url: ingestUrl, source: 'ingest_deeplink' });
        } catch (_err) {
            // analytics is best-effort; never block the import on telemetry
        }
    }, [dispatch, isImporting]);
}
